import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { ShieldCheck, KeyRound, Lock, Users } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import MembersManager from "@/components/MembersManager";
import InvitesManager from "@/components/InvitesManager";
import ChangePasswordCard from "@/components/ChangePasswordCard";

export default function Settings() {
  const { user } = useAuth();
  const { hash } = useLocation();
  const isOwner = user?.role === "owner" || user?.role === "admin";

  useEffect(() => {
    if (!isOwner || hash !== "#family-invites") return;
    const scrollToInvites = () => {
      document.getElementById("family-invites")?.scrollIntoView({ behavior: "auto", block: "start" });
    };
    const frame = window.requestAnimationFrame(scrollToInvites);
    const timers = [250, 700, 1200].map((delay) => window.setTimeout(scrollToInvites, delay));
    return () => {
      window.cancelAnimationFrame(frame);
      timers.forEach(window.clearTimeout);
    };
  }, [hash, isOwner]);
  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <p className="text-xs uppercase tracking-[0.08em] text-[color:var(--vault-text-tertiary)]">Settings</p>
        <h1 className="font-[Outfit] text-4xl tracking-tight font-semibold mt-1">Account & security</h1>
      </div>

      <div className="vault-card p-6">
        <h2 className="font-[Outfit] text-xl font-medium flex items-center gap-2"><Users size={18} /> Profile</h2>
        <dl className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
          <div>
            <dt className="text-xs uppercase tracking-[0.05em] text-[color:var(--vault-text-tertiary)]">Name</dt>
            <dd className="mt-1">{user?.name || "—"}</dd>
          </div>
          <div>
            <dt className="text-xs uppercase tracking-[0.05em] text-[color:var(--vault-text-tertiary)]">Email</dt>
            <dd className="mt-1">{user?.email}</dd>
          </div>
          <div>
            <dt className="text-xs uppercase tracking-[0.05em] text-[color:var(--vault-text-tertiary)]">Role</dt>
            <dd className="mt-1 capitalize">{user?.role}</dd>
          </div>
          <div>
            <dt className="text-xs uppercase tracking-[0.05em] text-[color:var(--vault-text-tertiary)]">Member since</dt>
            <dd className="mt-1">{user?.created_at ? new Date(user.created_at).toLocaleDateString() : "—"}</dd>
          </div>
        </dl>
      </div>

      <ChangePasswordCard />

      {isOwner && <MembersManager />}
      {isOwner && (
        <section id="family-invites" className="scroll-mt-28" data-testid="family-invites-section">
          <InvitesManager />
        </section>
      )}

      <div className="vault-card p-6">
        <h2 className="font-[Outfit] text-xl font-medium flex items-center gap-2"><Lock size={18} /> Security model</h2>
        <ul className="mt-4 space-y-3 text-sm text-[color:var(--vault-text-secondary)]">
          <li className="flex gap-3">
            <ShieldCheck size={18} className="text-[color:var(--vault-success)] shrink-0 mt-0.5" />
            <div>
              <strong className="text-[color:var(--vault-text-primary)]">Master password.</strong> Hashed with bcrypt — only you know it. We never see your master password.
            </div>
          </li>
          <li className="flex gap-3">
            <KeyRound size={18} className="text-[color:var(--vault-success)] shrink-0 mt-0.5" />
            <div>
              <strong className="text-[color:var(--vault-text-primary)]">AES‑256‑GCM at rest.</strong> Vault passwords and secure notes are encrypted in the database with a server-managed key.
            </div>
          </li>
          <li className="flex gap-3">
            <Users size={18} className="text-[color:var(--vault-success)] shrink-0 mt-0.5" />
            <div>
              <strong className="text-[color:var(--vault-text-primary)]">Sharing & emergency access.</strong> Pick exactly which items to share — and which contacts can reach your vault if something happens to you.
            </div>
          </li>
        </ul>
      </div>
    </div>
  );
}
